import React, { useState, useEffect, useCallback, useRef } from 'react';
import { EtiquetaTentativa, RespuestaTentativa } from './FechaTentativa';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import DeslizarAcciones from '../ui/DeslizarAcciones';
import ContactarClienteSheet from './ContactarClienteSheet';
import { LuCircleCheck, LuPartyPopper, LuClipboardList, LuCar, LuMapPin, LuUndo2, LuBuilding2, LuBanknote, LuStickyNote, LuMessageSquare, LuCalendarX, LuChevronDown, LuChevronUp, LuCheck } from 'react-icons/lu';
import { useOrdenes } from '../../hooks/useOrdenes';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import EjecutarOrdenSheet from '../servicio/EjecutarOrdenSheet';
import { getTodayISO, fechaAR } from '../../utils/dateUtils';
import ModalRegistrarTrabajo from './ModalRegistrarTrabajo';
import { buildGoogleMapsRouteUrl } from '../../utils/clienteUtils';
import DireccionMapa from '../ui/DireccionMapa';
import ConfirmarHorarioSheet from '../servicio/ConfirmarHorarioSheet';
import { resumenVentanas, horaOrden, horaTexto, claveAgenda } from '../../utils/ordenes';
import SalidaTecnicoSheet from './SalidaTecnicoSheet';
import { enviarOEncolar } from '../../utils/pendientesOffline';
import QueLlevarHoy from './QueLlevarHoy';
import RecorridoHoy from './RecorridoHoy';
import CargaPorSerieSheet from './CargaPorSerieSheet';
import { claveBorrador, hayBorrador } from '../../utils/borradores';
import ConversacionEmpresa from './ConversacionEmpresa';
import ChatVisita from './ChatVisita';
import EquiposDeVisita from './EquiposDeVisita';
import { etapaColor, etapaDeEstado, estiloEtiqueta } from '../../utils/estados';
import { PAGINA, PantallaHeader, BotonHerramienta, Herramientas } from '../ui/Pantalla';
import AntesDeIr from './AntesDeIr';
import { irA } from '../../utils/navegacion';
import MiRecorridoSheet from './MiRecorridoSheet';
import MapaDelDia from './MapaDelDia';
import VisitaResumen from './VisitaResumen';

const PRIORIDAD_COLOR = {
    BAJA:    { bg: 'bg-chip', tx: 'text-muted' },
    NORMAL:  { bg: 'bg-[#DBEAFE] dark:bg-[#1E3A5F]', tx: 'text-[#2563EB] dark:text-[#60A5FA]' },
    ALTA:    { bg: 'bg-[var(--warning-bg)]',           tx: 'text-[var(--warning-tx)]' },
    URGENTE: { bg: 'bg-[var(--danger-bg)]',            tx: 'text-[var(--danger-tx)]' },
};

const ESTADO_LABEL = {
    PENDIENTE: 'Asignado', EN_CAMINO: 'En camino', EN_SITIO: 'En el lugar',
    COMPLETADA: 'Hecho', CANCELADA: 'Devuelta', NO_ATENDIDO: 'No atendió',
};

// Mismos colores de etapa que ve el admin en Trabajos (utils/estados.js · ETAPAS)
const BORDER_COLOR = {
    PENDIENTE:   etapaColor('PENDIENTE'),
    EN_CAMINO:   etapaColor('EN_CAMINO'),
    EN_SITIO:    etapaColor('EN_SITIO'),
    COMPLETADA:  etapaColor('COMPLETADA'),
    CANCELADA:   'var(--etapa-hacer)',
    NO_ATENDIDO: 'var(--alerta)',
};

// El color del boton ya no varia por etapa (antes: azul/ambar/verde) — es
// siempre la misma accion "avanzar el siguiente paso", y la etapa actual ya
// se lee en el borde/dot de la card (BORDER_COLOR). Mismo criterio que se
// aplico a ServicioCard.jsx.
const SIGUIENTE_ESTADO = {
    // Opción 1 de color (3-oct-2026): salir y llegar = azul "en marcha"; cerrar = verde "listo"
    PENDIENTE:  { estado: 'EN_CAMINO', label: 'Salir', bg: 'var(--etapa-marcha)', Icon: LuCar },
    EN_CAMINO:  { estado: 'EN_SITIO',  label: 'Llegué', bg: 'var(--etapa-marcha)', Icon: LuMapPin },
    EN_SITIO:   { estado: 'COMPLETADA', label: 'Completar', bg: 'var(--etapa-listo)', Icon: LuCircleCheck },
};

// Paso atrás por si el técnico tocó la orden equivocada (solo uno, y nunca desde COMPLETADA)
const ESTADO_ANTERIOR = {
    EN_CAMINO: { estado: 'PENDIENTE', label: 'Deshacer "Salí"' },
    EN_SITIO:  { estado: 'EN_CAMINO', label: 'Deshacer "Llegué"' },
};

// La visita se cerró hoy: todavía se le pueden sumar equipos que faltaron
const cerradoHoy = (o) => {
    if (!o?.fechaCompletada) return false;
    const d = new Date(o.fechaCompletada);
    const h = new Date();
    return d.getFullYear() === h.getFullYear() && d.getMonth() === h.getMonth() && d.getDate() === h.getDate();
};

function OrdenCard({ orden, compacta, onAvanzar, onEjecutar, onRegistrarTrabajo, onProblema, onConfirmar, onVerServicio, onHorarioConfirmado, seleccionando, seleccionada, onToggleSel, onPlegar }) {
    // Cierre por N/S empezado y guardado en el celu (22-oct-2026): se ve cuánto falta
    const cargaEmpezada = (() => {
        if (!orden.tarifaMensual || orden.estado !== 'EN_SITIO') return null;
        const b = hayBorrador(claveBorrador(`carga_serie_${orden.id}`));
        if (!b?.items?.length) return null;
        return { total: b.items.length, listos: b.items.filter(it => it.listo).length };
    })();
    const [expandido, setExpandido] = useState(false);
    const [confirmandoHorario, setConfirmandoHorario] = useState(false);
    const [contactando, setContactando] = useState(false);
    const aCoordinar = !!orden.horarioACoordinar;
    // Fecha tentativa: el cliente no confirmó todavía → no se sale ni se da "Ok, voy"
    const tentativa = !!orden.sinConfirmar && orden.estado === 'PENDIENTE';

    const pr  = PRIORIDAD_COLOR[orden.prioridad] || PRIORIDAD_COLOR.NORMAL;
    const sig = SIGUIENTE_ESTADO[orden.estado];
    const esFinal = orden.estado === 'COMPLETADA' || orden.estado === 'CANCELADA';

    // Deslizar la tarjeta (5-oct-2026): → hace el paso que sigue, ← abre el mapa
    const fechaOk = !orden.fechaProgramada || orden.fechaProgramada >= getTodayISO();
    // 31-oct-2026: "Salir" solo el día de la visita (o si quedó atrasada): antes salía también en las de la semana
    const esDelDia = !orden.fechaProgramada || orden.fechaProgramada <= getTodayISO();
    const puedeAvanzar = !!sig && (orden.estado !== 'PENDIENTE' || esDelDia);
    const pideOkVoy = !aCoordinar && !tentativa && orden.estado === 'PENDIENTE' && !orden.confirmadaEn && !!onConfirmar && fechaOk;
    const listo = 'var(--etapa-listo)';
    let derecha = null;
    if (!esFinal && !seleccionando && !tentativa) {
        if (aCoordinar) derecha = { label: 'Confirmar día y hora', color: listo, Icon: LuCircleCheck, accion: () => setConfirmandoHorario(true) };
        else if (orden.estado === 'PENDIENTE' && !orden.confirmadaEn && onConfirmar && fechaOk) derecha = { label: 'Ok, voy', color: listo, Icon: LuCircleCheck, accion: () => onConfirmar(orden) };
        else if (orden.estado === 'EN_SITIO') derecha = { label: 'Cerrar trabajo', color: listo, Icon: LuCircleCheck, accion: () => (orden.presupuestoId ? onEjecutar(orden) : onRegistrarTrabajo(orden)) };
        else if (puedeAvanzar) derecha = { label: sig.label, color: sig.bg, Icon: sig.Icon, accion: () => onAvanzar(orden.id, sig.estado) };
    }
    const izquierda = !esFinal && !seleccionando && orden.direccion
        ? { label: 'Mapa', color: '#3B82F6', Icon: LuMapPin, accion: () => irA({ direccion: orden.direccion, nombre: orden.lugar || orden.clienteNombre }) }
        : null;

    return (
        <DeslizarAcciones derecha={derecha} izquierda={izquierda}>
        <div id={`orden-${orden.id}`} className={`rounded-2xl overflow-clip bg-card border border-black/10 dark:border-white/[0.12] transition-all ${seleccionando && seleccionada ? 'ring-2 ring-brand-red' : ''} ${['EN_CAMINO', 'EN_SITIO'].includes(orden.estado) ? 'shadow-lg' : ''}`}
            style={{ borderLeft: `6px solid ${tentativa ? 'var(--warning-tx)' : BORDER_COLOR[orden.estado] || '#A8A29E'}` }}
            onClick={seleccionando ? () => onToggleSel(orden.id) : undefined}>
            <div className="p-4">
                {/* 31-oct-2026: una visita de otro día que se abrió se vuelve a achicar tocando arriba, como se abrió */}
                <div className={`flex items-start gap-2 mb-2 ${onPlegar ? 'cursor-pointer' : ''}`}
                    onClick={onPlegar ? (e) => { e.stopPropagation(); onPlegar(); } : undefined}>
                    {seleccionando && (
                        <div className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${seleccionada ? 'bg-brand-red border-brand-red' : 'border-muted bg-transparent'}`}>
                            {seleccionada && <span className="text-white text-label font-black">✓</span>}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            {ESTADO_LABEL[orden.estado] && (
                                <span className="text-label font-black px-2 py-0.5 rounded-md uppercase"
                                    style={etapaDeEstado(orden.estado) ? estiloEtiqueta(etapaDeEstado(orden.estado)) : { background: BORDER_COLOR[orden.estado], color: '#fff' }}>
                                    {ESTADO_LABEL[orden.estado]}
                                </span>
                            )}
                            {tentativa && <EtiquetaTentativa />}
                            {orden.estado === 'PENDIENTE' && orden.confirmadaEn && (
                                <span className="text-label font-black px-2 py-0.5 rounded-md bg-chip text-secondary">✓ Confirmada</span>
                            )}
                            {orden.prioridad && orden.prioridad !== 'NORMAL' && (
                                <span className={`text-label font-black px-2 py-0.5 rounded-md uppercase ${pr.bg} ${pr.tx}`}>
                                    {orden.prioridad}
                                </span>
                            )}
                        </div>
                        {!compacta && <p className="font-black text-body-lg text-ink leading-tight">{orden.titulo}</p>}
                    </div>
                    {/* Dentro del recorrido: nombre, dirección y hora ya están en la fila de arriba */}
                    {compacta ? (orden.direccion && !esFinal && (
                        <button type="button" onClick={e => { e.stopPropagation(); izquierda?.accion(); }}
                            className="h-9 px-3 shrink-0 rounded-xl bg-chip text-label font-black text-ink inline-flex items-center gap-1.5 active:scale-95">
                            <LuMapPin size={14} /> Mapa
                        </button>
                    )) : (
                    <div className="text-right shrink-0">
                        {aCoordinar ? <p className="text-body font-black text-ink">A coordinar</p>
                            : orden.horaEstimada ? <p className="text-body font-black text-ink">{horaTexto(orden.horaEstimada)}</p>
                            : <p className="text-caption font-bold text-muted">Sin horario</p>}
                        {/* 29-oct-2026: a coordinar, la fecha es una referencia (no está confirmada) */}
                        <p className="text-caption text-muted">{aCoordinar ? `Sugerido: ${fechaAR(orden.fechaProgramada)}` : fechaAR(orden.fechaProgramada)}</p>
                    </div>
                    )}
                </div>

                {!esFinal && !compacta && orden.clienteNombre && !String(orden.titulo || '').includes(orden.clienteNombre) && (
                    <p className="text-body text-secondary font-bold flex items-center gap-1"><LuBuilding2 size={13} />{orden.clienteNombre}</p>
                )}

                {/* Visita de cliente con tarifa mensual: los equipos a atender */}
                {!esFinal && orden.equiposSerie && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                        {orden.equiposSerie.split(',').filter(Boolean).map(ns => (
                            <span key={ns} className="px-2 py-0.5 rounded-md bg-chip text-label font-black text-ink">{ns}</span>
                        ))}
                    </div>
                )}

                {!esFinal && orden.montoEstimado && (
                    <p className="text-body font-black text-brand-amber mt-0.5 flex items-center gap-1">
                        <LuBanknote size={14} /> ${Number(orden.montoEstimado).toLocaleString('es-AR')} · {orden.formaPago === 'TRANSFERENCIA' ? 'Transferencia' : 'Efectivo'}
                    </p>
                )}

                {!esFinal && !compacta && orden.direccion && (
                    <DireccionMapa direccion={orden.direccion} className="mt-1.5" />
                )}

                {/* Horario pedido por el cliente (presupuesto con fecha tentativa):
                    antes el técnico no lo veía en ningún lado. Confirmar acá fija
                    día y hora en el presupuesto y en esta orden. */}
                {!esFinal && aCoordinar && (
                    <div className="mt-2.5 p-3 rounded-xl border border-[var(--estado-curso)] bg-[var(--warning-bg)]">
                        <p className="text-label font-bold text-[var(--warning-tx)]">El cliente puede</p>
                        {resumenVentanas(orden.ventanasCliente).map(linea => (
                            <p key={linea} className="text-body font-bold text-ink">{linea}</p>
                        ))}
                        <button onClick={e => { e.stopPropagation(); setConfirmandoHorario(true); }}
                            className="mt-2 w-full h-11 rounded-xl font-black text-body text-ink bg-card border-2 border-[color:var(--etapa-listo)] active:scale-95">
                            ✓ Confirmar día y hora
                        </button>
                        <p className="mt-1.5 text-caption text-muted">Primero elegí cuándo vas; con eso el admin ya sabe que confirmaste.</p>
                    </div>
                )}
                {!esFinal && tentativa && <RespuestaTentativa orden={orden} onCambio={onHorarioConfirmado} />}
                {contactando && <ContactarClienteSheet orden={orden} onCerrar={() => setContactando(false)} />}
                {confirmandoHorario && (
                    <ConfirmarHorarioSheet
                        servicio={{ id: orden.presupuestoId, ventanasDisponibles: orden.ventanasCliente, clienteNombre: orden.clienteNombre }}
                        url={orden.presupuestoId ? null : `/ordenes/${orden.id}/confirmar-horario`}
                        onCerrar={() => setConfirmandoHorario(false)}
                        onConfirmado={() => { setConfirmandoHorario(false); onHorarioConfirmado && onHorarioConfirmado(); }}
                    />
                )}

                {!esFinal && <AntesDeIr ordenId={orden.id} className="mt-3" />}
                {!esFinal && <EquiposDeVisita ordenId={orden.id} />}
                <ChatVisita orden={orden} soyAdmin={false} className="mt-3" />
                {!esFinal && /^Pedido #\d+/.test(orden.descripcion || '') && <ConversacionEmpresa ordenId={orden.id} />}

                {!esFinal && orden.descripcion && (
                    <>
                        <button onClick={() => setExpandido(v => !v)}
                            className="mt-3 w-full flex items-center justify-between px-3 py-2 rounded-xl text-label font-bold bg-panel text-secondary active:scale-95 transition-all">
                            <span>Instrucciones</span>
                            {expandido ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}
                        </button>
                        {expandido && (
                            <div className="mt-2 p-3 rounded-xl bg-panel">
                                <p className="text-caption text-secondary leading-snug">{orden.descripcion}</p>
                            </div>
                        )}
                    </>
                )}

                {orden.notasTecnico && (
                    <p className="mt-2 text-caption text-brand-green flex items-center gap-1">
                        <LuStickyNote size={12} />{orden.notasTecnico}
                    </p>
                )}

                {/* Link al trabajo de la visita (el del presupuesto o el que se cargó al cerrar) */}
                {esFinal && orden.estado === 'COMPLETADA' && (orden.servicioId || orden.presupuestoId) && (
                    <button onClick={() => onVerServicio(orden)}
                        className="mt-2 text-label font-bold text-[#3B82F6] dark:text-[#60A5FA] active:opacity-60">
                        Ver servicio →
                    </button>
                )}
            </div>

            {!esFinal && sig && !seleccionando && (
                // Fijo abajo mientras la tarjeta está a la vista (23-oct-2026, testeo U4): el paso que sigue
                // quedaba debajo de equipos, chat e instrucciones, y la barra de abajo lo tapaba.
                // overflow-clip (no hidden) en la tarjeta para que sticky funcione.
                <div className="sticky bottom-16 md:bottom-0 z-10 flex flex-col gap-2 px-4 py-3 bg-panel border-t border-black/[0.06] dark:border-white/[0.06]" onClick={e => e.stopPropagation()}>
                    {/* "Ok, voy" (5-oct-2026): el admin ve quién confirmó su visita */}
                    {/* Con fecha "a coordinar" el paso es Confirmar día y hora (arriba): sin "Ok, voy" ni "Salir" (5-oct-2026) */}
                    {/* Un solo botón grande, el paso que sigue: primero "Ok, voy"; recién después "Salir" (5-oct-2026) */}
                    {pideOkVoy && (
                        <button onClick={() => onConfirmar(orden)}
                            className="w-full py-2.5 rounded-xl font-black text-body text-ink bg-card border-2 border-[color:var(--etapa-listo)] active:scale-95 transition-all">
                            ✓ Ok, voy
                        </button>
                    )}
                    {orden.estado === 'EN_SITIO' ? (
                        <>
                            {/* Un solo botón con un solo texto — antes decía "Registrar trabajo"
                                o "Ejecutar trabajo" según un dato invisible (si venía de un
                                presupuesto). Adentro se decide solo qué formulario abrir. */}
                            <button onClick={() => orden.presupuestoId ? onEjecutar(orden) : onRegistrarTrabajo(orden)}
                                style={{ background: 'var(--etapa-listo)' }}
                                className="w-full py-2.5 rounded-xl font-black text-body text-white active:scale-95 transition-all">
                                {cargaEmpezada ? 'Seguir cargando' : 'Cerrar trabajo'}
                            </button>
                            <p className="text-caption text-center text-muted font-bold">
                                {cargaEmpezada
                                    ? `Carga empezada: ${cargaEmpezada.listos}/${cargaEmpezada.total} equipos listos`
                                    : orden.tarifaMensual ? 'Cargás los equipos de a uno; se guarda solo en el teléfono'
                                        : 'Completá los datos del trabajo para cerrar la orden'}
                            </p>
                        </>
                    ) : aCoordinar || pideOkVoy || tentativa || !puedeAvanzar ? null : (
                        <button onClick={() => onAvanzar(orden.id, sig.estado)}
                            style={{ background: sig.bg }}
                            className="w-full py-2.5 rounded-xl font-black text-body text-white active:scale-95 transition-all flex items-center justify-center gap-1.5">
                            <sig.Icon size={16} /> {sig.label}
                        </button>
                    )}
                    {/* Todo lo que sale mal, en un solo lugar (4-oct-2026): antes eran
                        3 botones sueltos (no atendió / no puedo ir / volver atrás). */}
                    <div className="grid grid-cols-2 gap-2">
                        {/* Contactar al cliente pasa por el admin: el técnico no tiene el teléfono (5-oct-2026) */}
                        <button onClick={() => setContactando(true)}
                            className="py-2 rounded-xl font-bold text-label text-secondary bg-chip active:scale-95 transition-all inline-flex items-center justify-center gap-1.5">
                            <LuMessageSquare size={14} /> Contactar al cliente
                        </button>
                        <button onClick={() => onProblema(orden)}
                            className="py-2 rounded-xl font-bold text-label text-muted bg-chip active:scale-95 transition-all">
                            Hubo un problema
                        </button>
                    </div>
                </div>
            )}
        </div>
        </DeslizarAcciones>
    );
}

// Visita de un pedido de empresa: se cierra con la carga por N/S (sin cobro, va al
// cierre mensual), no con "¿Te pagó?" (10-oct-2026)

export default function MisOrdenes({ tecnicoId, onEjecutarOrden }) {
    const { ordenes, cargando, avanzarEstado, recargar, marcarLocal } = useOrdenes({ tecnicoId });
    const tab = 'activas'; // sin pestañas (4-oct-2026): agenda/completadas/rendimiento → "Mi mes"
    const [problema, setProblema] = useState(null); // orden con "Hubo un problema" abierto
    // 31-oct-2026: los días que vienen se ven abiertos, con cada visita en dos renglones (VisitaResumen);
    // el técnico puede plegar un día, y tocando una visita se abre la tarjeta completa.
    const [diasPlegados, setDiasPlegados] = useState(() => new Set());
    const [visitasAbiertas, setVisitasAbiertas] = useState(() => new Set());
    const abrirVisita = (id) => setVisitasAbiertas(prev => new Set(prev).add(id));
    const cerrarVisita = (id) => setVisitasAbiertas(prev => { const n = new Set(prev); n.delete(id); return n; });
    const [verAtrasadas, setVerAtrasadas] = useState(null); // null = automático (abiertas si hoy no hay nada)

    // "Elegir para ruta": mismo patron que ya tiene Presupuestos (admin) —
    // el tecnico marca varias visitas pendientes y arma una sola ruta con
    // todas las paradas en Google Maps, en vez de abrir "Ver ruta" de a una
    // y tener que adivinar el mejor orden a mano.
    const [modoSeleccion, setModoSeleccion] = useState(false);
    const [miRecorrido, setMiRecorrido] = useState(false);
    const [seleccionados, setSeleccionados] = useState(new Set());
    const toggleSeleccion = (id) => {
        setSeleccionados(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const abrirRuta = (items) => {
        const url = buildGoogleMapsRouteUrl(items.map(o => o.direccion));
        if (!url) { toast.error('Ninguna de las elegidas tiene dirección cargada'); return; }
        window.open(url, '_blank');
    };

    const [historial,        setHistorial]        = useState([]);
    const [, setCargandoHistorial] = useState(false);

    const cargarHistorial = useCallback(() => {
        if (!tecnicoId) return;
        setCargandoHistorial(true);
        api.get(`/ordenes/historial/${tecnicoId}`)
            .then(r => setHistorial((r.data || []).filter(o => o.estado === 'COMPLETADA')))
            .catch(() => setHistorial([]))
            .finally(() => setCargandoHistorial(false));
    }, [tecnicoId]);

    // Cargar historial al montar (resumen del dia) y al cambiar a tab historial
    useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

    // Llegó tocando una notificación: mostrar y resaltar esa visita (5-oct-2026)
    useEffect(() => {
        const resaltar = () => {
            let id = null;
            try { id = sessionStorage.getItem('resaltarOrden'); } catch { /* */ }
            if (!id) return;
            const o = ordenes.find(x => String(x.id) === id);
            if (!o) return; // todavía no cargó, o ya no está activa
            try { sessionStorage.removeItem('resaltarOrden'); } catch { /* */ }
            if (o.fechaProgramada) setDiasPlegados(prev => { const n = new Set(prev); n.delete(o.fechaProgramada); return n; });
            setVisitasAbiertas(prev => new Set(prev).add(o.id));
            setTimeout(() => {
                const el = document.getElementById(`orden-${id}`);
                if (!el) return;
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('ring-4', 'ring-[#C9341F]');
                setTimeout(() => el.classList.remove('ring-4', 'ring-[#C9341F]'), 2500);
            }, 150);
        };
        resaltar();
        window.addEventListener('resaltar-orden', resaltar);
        return () => window.removeEventListener('resaltar-orden', resaltar);
    }, [ordenes]);

    const [servicioEjecutando, setServicioEjecutando] = useState(null);
    const [ordenEjecutandoId, setOrdenEjecutandoId] = useState(null);
    const [ordenRegistrando, setOrdenRegistrando] = useState(null);
    const [noAtendidoOrden, setNoAtendidoOrden] = useState(null);
    const [cargaSerie, setCargaSerie] = useState(false);
    const [salida, setSalida] = useState(null); // { modo: 'orden'|'hoy'|'mensaje', orden? }
    const [notaNoAtendido, setNotaNoAtendido] = useState('');
    const [servicioDetalle, setServicioDetalle] = useState(null);
    const [cargandoDetalle, setCargandoDetalle] = useState(false);

    const [sumandoA, setSumandoA] = useState(null);   // { orden, servicio }: sumar equipos a un trabajo cerrado hoy
    const verServicio = async (orden) => {
        setCargandoDetalle(true);
        try {
            const res = await api.get(`/servicios/${orden.servicioId || orden.presupuestoId}`);
            setServicioDetalle({ ...res.data, _orden: orden });
        } catch {
            toast.error('No se pudo cargar el servicio');
        } finally {
            setCargandoDetalle(false);
        }
    };

    const handleNoAtendido = async () => {
        if (!noAtendidoOrden) return;
        try {
            await api.patch(`/ordenes/${noAtendidoOrden.id}/estado`, {
                estado: 'NO_ATENDIDO',
                notasTecnico: notaNoAtendido.trim() || 'No atendido',
            });
            toast.success('Orden devuelta al admin');
            setNoAtendidoOrden(null);
            setNotaNoAtendido('');
            if (recargar) recargar();
        } catch {
            toast.error('Error al reportar');
        }
    };

    const handleEjecutar = async (orden) => {
        try {
            const res = await api.get(`/servicios/${orden.presupuestoId}`);
            setServicioEjecutando(res.data);
            setOrdenEjecutandoId(orden.id);
        } catch {
            toast.error('No se pudo cargar el servicio. Intentá de nuevo.');
        }
    };

    // La orden se marca COMPLETADA apenas se guarda el trabajo (antes era al tocar
    // "Listo" y si el técnico cerraba la pantalla quedaba abierta).
    const completarOrdenEjecutando = async () => {
        if (!ordenEjecutandoId) return;
        try {
            const r = await enviarOEncolar('patch', `/ordenes/${ordenEjecutandoId}/estado`, { estado: 'COMPLETADA' }, `Visita #${ordenEjecutandoId} → COMPLETADA`);
            if (r.encolado) toast('Sin señal: la visita se marca completada cuando vuelva la conexión', { icon: '📶' });
        } catch (e) {
            const det = e?.response?.data?.mensaje || e?.message || '';
            toast.error(`No se pudo completar la orden${det ? ': ' + det : ''}. Avisá al admin.`);
        }
    };

    // "Ok, voy": el técnico confirma la visita que le asignaron
    const confirmadas = useRef({ n: 0, t: null });
    const confirmarVisita = async (o) => {
        try {
            await api.patch(`/ordenes/${o.id}/confirmar`);
            // 31-oct-2026: confirmando varias seguidas se apilaban avisos iguales; ahora es uno solo que cuenta
            const c = confirmadas.current;
            c.n += 1;
            clearTimeout(c.t);
            c.t = setTimeout(() => { c.n = 0; }, 6000);
            toast.success(c.n > 1 ? `Listo, confirmaste ${c.n} visitas` : 'Listo, el admin ya sabe que vas', { id: 'ok-voy', duration: 2500 });
            if (recargar) recargar();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo confirmar'); }
    };

    const handleConfirmado = () => {
        setServicioEjecutando(null);
        setOrdenEjecutandoId(null);
        if (recargar) recargar();
        cargarHistorial();
    };

    const activas = ordenes.filter(o => !['COMPLETADA','CANCELADA','NO_ATENDIDO'].includes(o.estado));
    // Criterio de orden (2-oct-2026): lo pendiente como agenda (próximo primero, ya viene
    // así del backend); lo terminado como historial (lo más nuevo primero).
    const historialOrdenado = [...historial].sort((a, b) =>
        `${b.fechaProgramada || ''} ${horaOrden(b.horaEstimada)}`.localeCompare(`${a.fechaProgramada || ''} ${horaOrden(a.horaEstimada)}`));
    const lista   = tab === 'activas' ? activas : historialOrdenado;

    // "Todos"/"Ninguno" — mismo patrón que useRepuestoManager.js / ServicioManager.jsx:
    // selecciona todas las visitas activas visibles, no un listado sin filtrar.
    const todosSeleccionados = activas.length > 0 && seleccionados.size === activas.length;
    const seleccionarTodos = () => {
        setSeleccionados(todosSeleccionados ? new Set() : new Set(activas.map(o => o.id)));
    };

    // Resumen del dia
    const fechaLarga = (() => { const t = new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' }); return t.charAt(0).toUpperCase() + t.slice(1); })();
    const ordenesHoy = activas.filter(o => o.fechaProgramada === getTodayISO());
    const completadasHoy = historial.filter(o => o.estado === 'COMPLETADA' && o.fechaProgramada === getTodayISO());

    // Las visitas con el día "a coordinar" van primero, abiertas: el técnico tiene que
    // elegir el día (antes quedaban plegadas en el día provisorio — 10-oct-2026)
    const A_COORDINAR = 'a-coordinar';
    const porFecha = lista.reduce((acc, o) => {
        const k = tab === 'activas' && o.horarioACoordinar ? A_COORDINAR : o.fechaProgramada;
        if (!acc[k]) acc[k] = [];
        acc[k].push(o);
        return acc;
    }, {});
    const grupos = Object.entries(porFecha).sort(([a], [b]) => (a === A_COORDINAR ? -1 : b === A_COORDINAR ? 1 : 0));

    const hoy = getTodayISO();
    // Orden de Hoy (13-oct-2026): primero lo que hay que coordinar, después el recorrido de hoy,
    // lo de días anteriores junto y plegado, y los días que vienen. Eligiendo para ruta se ve todo suelto.
    const ATRASADAS = '__atrasadas';
    const enActivas = tab === 'activas' && !modoSeleccion;
    const paradasHoy = enActivas ? [
        ...(porFecha[hoy] || []).map(o => ({ orden: o, hecha: false })),
        ...completadasHoy.map(o => ({ orden: o, hecha: true })),
    ].sort((x, y) => claveAgenda(x.orden.horaEstimada, x.orden.ordenDia, x.orden.id).localeCompare(claveAgenda(y.orden.horaEstimada, y.orden.ordenDia, y.orden.id))) : [];
    const recorridoHoy = paradasHoy.length > 1 && porFecha[hoy]?.length ? paradasHoy : null;
    const secciones = !enActivas ? grupos : [
        ...grupos.filter(([f]) => f === A_COORDINAR),
        ...grupos.filter(([f]) => f === hoy && !recorridoHoy),
        ...(() => { const at = grupos.filter(([f]) => f !== A_COORDINAR && f < hoy).flatMap(([, it]) => it); return at.length ? [[ATRASADAS, at]] : []; })(),
        ...grupos.filter(([f]) => f !== A_COORDINAR && f > hoy),
    ];
    // Desde el mapa del día: ir a la tarjeta de esa visita (29-oct-2026)
    const irAOrden = (id) => {
        abrirVisita(id);   // 1-nov-2026: si es de otro día y estaba en dos renglones, se abre completa
        const el = document.getElementById(`orden-${id}`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-[#D48800]'); setTimeout(() => el.classList.remove('ring-2', 'ring-[#D48800]'), 2000); }
        else toast('Está en tu lista, más abajo');
    };
    const tarjeta = (o, compacta, onPlegar) => (
        <div key={o.id} id={`orden-${o.id}`} className="rounded-2xl transition-shadow"><OrdenCard orden={o} compacta={compacta === true} onHorarioConfirmado={recargar} onAvanzar={avanzarEstado} onEjecutar={handleEjecutar} onRegistrarTrabajo={(x) => (x.tarifaMensual ? setCargaSerie(x) : setOrdenRegistrando(x))} onProblema={setProblema} onConfirmar={confirmarVisita} onVerServicio={verServicio} onPlegar={onPlegar}
            seleccionando={modoSeleccion} seleccionada={seleccionados.has(o.id)} onToggleSel={toggleSeleccion} /></div>
    );
    const formatFecha = (f) => {
        if (f === hoy) return 'Hoy';
        const d = new Date(f + 'T00:00:00');
        return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    };



    const pull = usePullToRefresh(() => recargar?.());

    return (
        <>
        <div className={PAGINA} {...pull.handlers}>
            {pull.indicador}
            <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6">
                {/* Header */}
                {modoSeleccion ? (
                    <div className="mb-4 flex items-center justify-between gap-2">
                        <div>
                            <p className="text-body-lg font-black text-ink">
                                {seleccionados.size} seleccionada{seleccionados.size !== 1 ? 's' : ''}
                            </p>
                            <p className="text-caption text-muted">Tocá las visitas que querés incluir</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button onClick={seleccionarTodos}
                                className="h-9 px-3 rounded-xl font-bold text-label text-secondary bg-chip active:scale-95 transition-all">
                                {todosSeleccionados ? 'Ninguno' : 'Todos'}
                            </button>
                            {seleccionados.size > 0 && (
                                <button onClick={() => abrirRuta(activas.filter(o => seleccionados.has(o.id)))}
                                    className="h-9 px-3 rounded-xl font-black text-label text-white bg-brand-red active:scale-95 transition-all flex items-center gap-1">
                                    <LuMapPin size={14} /> Ver ruta
                                </button>
                            )}
                            <button onClick={() => { setModoSeleccion(false); setSeleccionados(new Set()); }}
                                className="h-9 px-3 rounded-xl font-bold text-label text-secondary bg-chip active:scale-95 transition-all">
                                Cancelar
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="mb-4">
                        <PantallaHeader subtituloEnCelular titulo="Hoy"
                            subtitulo={`${fechaLarga} · ${activas.length} pendiente${activas.length !== 1 ? 's' : ''}`}>
                            {/* 29-oct-2026: "Elegir para ruta" → "Mi recorrido" (mapa con el orden del día, solo ver y navegar) */}
                            {/* "Mi recorrido" ya no va acá: el mapa del día está abajo y "Horarios" abre el detalle */}
                        </PantallaHeader>
                    </div>
                )}

                {/* 29-oct-2026: el día arranca con el mapa (antes tres números: visitas, listas, próxima) */}
                {tab === 'activas' && (
                    <MapaDelDia tecnicoId={tecnicoId} recargarKey={[...activas, ...completadasHoy].map(o => `${o.id}${o.estado}`).join(',')}
                        onVerDetalle={() => setMiRecorrido(true)} onIrAOrden={irAOrden} onCambio={recargar} />
                )}

                {tab === 'activas' && <QueLlevarHoy ordenesHoy={ordenesHoy} />}

                {/* Herramientas del día (5-oct-2026): "Cargar N/S" e "Historial N/S" pasaron
                    al Panel del admin. La carga por N/S se abre sola al cerrar una visita con
                    equipos; el técnico solo trabaja lo que le asignan. */}
                {tab === 'activas' && (
                    <div className="mb-4">
                        <Herramientas>
                            <BotonHerramienta icono={LuMessageSquare} onClick={() => setSalida({ modo: 'mensaje' })} textoEnCelular>Avisar al admin</BotonHerramienta>
                            <BotonHerramienta icono={LuCalendarX} onClick={() => setSalida({ modo: 'hoy' })} textoEnCelular>No puedo trabajar hoy</BotonHerramienta>
                        </Herramientas>
                    </div>
                )}

                {/* Contenido */}
                {cargando ? (
                    <div className="flex flex-col gap-2">
                        {[1, 2, 3].map(i => <div key={i} className="h-28 rounded-2xl animate-pulse bg-card" />)}
                    </div>
                ) : lista.length === 0 ? (
                    <div className="text-center py-16 rounded-2xl bg-card border border-black/[0.07] dark:border-white/[0.07]">
                        {tab === 'activas' ? <LuPartyPopper size={32} className="mb-2 text-muted inline-block" /> : <LuClipboardList size={32} className="mb-2 text-muted inline-block" />}
                        <p className="text-body font-bold text-muted">
                            {tab === 'activas' ? 'Sin órdenes pendientes' : 'Sin historial aún'}
                        </p>
                    </div>
                ) : (
                    <>
                    {recorridoHoy && (
                        <RecorridoHoy paradas={recorridoHoy} horaOrden={horaOrden} onRuta={() => setMiRecorrido(true)} renderCard={o => tarjeta(o, true)} onVerHecha={verServicio} />
                    )}
                    {secciones
                        .map(([fecha, items]) => {
                        if (fecha === ATRASADAS) {
                            const abiertas = verAtrasadas ?? (!(recorridoHoy || porFecha[hoy]) || items.some(o => ['EN_CAMINO', 'EN_SITIO'].includes(o.estado)));
                            return (
                                <div key="atrasadas" className="mb-5">
                                    <button onClick={() => setVerAtrasadas(!abiertas)}
                                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-[var(--warning-bg)] mb-2 active:scale-[0.99]">
                                        <span className="text-label font-black uppercase tracking-wider text-[var(--warning-tx)]">De días anteriores</span>
                                        <span className="text-label font-bold text-[var(--warning-tx)] inline-flex items-center gap-1">{items.length} visita{items.length !== 1 ? 's' : ''} {abiertas ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}</span>
                                    </button>
                                    {abiertas && <div className="space-y-4">{items.map(o => tarjeta(o))}</div>}
                                </div>
                            );
                        }
                        // Hoy y lo atrasado, abierto. Los días que vienen, plegados (4-oct-2026)
                        const coordinar = fecha === A_COORDINAR;
                        const futuro = !coordinar && fecha > hoy;
                        const abierto = !futuro || !diasPlegados.has(fecha);
                        return (
                        <div key={fecha} className="mb-5">
                            {futuro ? (
                                <button onClick={() => setDiasPlegados(prev => { const n = new Set(prev); n.has(fecha) ? n.delete(fecha) : n.add(fecha); return n; })}
                                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-card border border-black/[0.06] dark:border-white/[0.06] mb-2 active:scale-[0.99]">
                                    <span className="text-label font-black text-muted uppercase tracking-wider capitalize">{formatFecha(fecha)}</span>
                                    <span className="text-label font-bold text-secondary inline-flex items-center gap-1">{items.length} visita{items.length !== 1 ? 's' : ''} {abierto ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}</span>
                                </button>
                            ) : (
                                <p className={`text-label font-black uppercase tracking-wider mb-2 ${coordinar ? 'text-[var(--warning-tx)]' : 'text-muted capitalize'}`}>
                                    {coordinar ? `Elegí el día · ${items.length} visita${items.length !== 1 ? 's' : ''}` : fecha < hoy ? `Atrasada · ${formatFecha(fecha)}` : formatFecha(fecha)}
                                </p>
                            )}
                            {abierto && (futuro
                                ? <div className="space-y-2">{items.map(o => (visitasAbiertas.has(o.id) ? (
                                    // Abierta: se puede volver a achicar (31-oct-2026)
                                    <div key={o.id}>
                                        <button type="button" onClick={() => cerrarVisita(o.id)}
                                            className="w-full h-9 flex items-center justify-end gap-1 px-1 text-label font-black text-muted active:opacity-60">
                                            Ver menos <LuChevronUp size={14} />
                                        </button>
                                        {tarjeta(o, false, () => cerrarVisita(o.id))}
                                    </div>
                                ) : <VisitaResumen key={o.id} orden={o} color={BORDER_COLOR[o.estado] || '#A8A29E'} onAbrir={abrirVisita} onConfirmar={confirmarVisita} />))}</div>
                                : <div className="space-y-4">{items.map(o => tarjeta(o))}</div>)}
                        </div>
                        );
                    })}
                    </>
                )}

                {/* Hechas hoy (21-oct-2026): para ver el trabajo y sumar equipos que faltaron.
                    Con recorrido del día ya aparecen ahí (tocando la parada hecha). */}
                {enActivas && !cargando && !recorridoHoy && completadasHoy.some(o => o.servicioId || o.presupuestoId) && (
                    <div className="mb-5">
                        <p className="text-label font-black uppercase tracking-wider text-muted mb-2">Hechas hoy</p>
                        <div className="space-y-2">
                            {completadasHoy.filter(o => o.servicioId || o.presupuestoId).map(o => (
                                <button key={o.id} type="button" onClick={() => verServicio(o)}
                                    className="w-full min-h-12 flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-card border border-black/[0.07] dark:border-white/[0.07] text-left active:scale-[0.99]">
                                    <span className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center bg-[#16A34A] text-white"><LuCheck size={14} /></span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-caption font-black text-ink truncate">{o.titulo?.replace(/^Visita · /, '') || o.clienteNombre}</span>
                                        <span className="block text-label text-muted truncate">{o.clienteNombre}</span>
                                    </span>
                                    <span className="shrink-0 text-label font-black text-brand-red">Ver · sumar equipos</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* 29-oct-2026: el día se cierra solo pasada la medianoche (finanzas/cierre_dia.py); antes había que
                    tocar "Cerrar mi día". Quien quiera mandar una nota igual, lo hace con "Avisar al admin". */}
                {tab === 'activas' && !cargando && (
                    <p className="mt-2 text-center text-caption text-muted">Tu día se cierra solo a la medianoche: al admin le llega el resumen y el efectivo a rendir.</p>
                )}
            </div>
        </div>

        {noAtendidoOrden && (
            <div className="fixed inset-0 z-[3000] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6"
                onClick={() => setNoAtendidoOrden(null)}>
                <div className="w-full max-w-md bg-card rounded-t-3xl md:rounded-3xl shadow-2xl p-5 space-y-4"
                    onClick={e => e.stopPropagation()}>
                    <div className="w-10 h-1 rounded-full mx-auto bg-chip md:hidden" />
                    <div>
                        <p className="text-label font-black text-muted uppercase tracking-widest mb-1">No atendido</p>
                        <p className="text-body-lg font-black text-ink">{noAtendidoOrden.titulo}</p>
                    </div>
                    <textarea value={notaNoAtendido} onChange={e => setNotaNoAtendido(e.target.value)}
                        rows={3} placeholder="Motivo (ej: no habia nadie, cerrado, no atendia el telefono...)"
                        className="w-full px-3 py-2.5 rounded-xl bg-chip text-ink text-body font-medium outline-none resize-none placeholder:text-muted" />
                    <div className="flex gap-2">
                        <button onClick={() => setNoAtendidoOrden(null)}
                            className="flex-1 py-3 rounded-2xl font-black text-label uppercase bg-chip text-secondary active:scale-95 transition-all">
                            Cancelar
                        </button>
                        <button onClick={handleNoAtendido}
                            className="flex-[2] py-3 rounded-2xl font-black text-label uppercase text-white bg-[#DC2626] active:scale-95 transition-all">
                            Confirmar
                        </button>
                    </div>
                </div>
            </div>
        )}

        {salida && (
            <SalidaTecnicoSheet modo={salida.modo} orden={salida.orden}
                onCerrar={() => setSalida(null)}
                onListo={() => { setSalida(null); if (recargar) recargar(); }} />
        )}

        {ordenRegistrando && (
            <ModalRegistrarTrabajo
                orden={ordenRegistrando}
                tecnicoId={tecnicoId}
                onGuardado={(r) => {
                    const id = ordenRegistrando.id;
                    setOrdenRegistrando(null);
                    // Sin señal: la visita ya se ve cerrada (se manda sola); con señal, se recarga
                    if (r?.encolado) marcarLocal(id, { estado: 'COMPLETADA' });
                    else if (recargar) recargar();
                }}
                onCerrar={() => setOrdenRegistrando(null)}
            />
        )}

        {cargaSerie && <CargaPorSerieSheet orden={cargaSerie === true ? null : cargaSerie}
            onClose={() => setCargaSerie(false)}
            onGuardado={() => {
                // Visita con equipos: al guardar la carga, la visita queda cerrada
                if (cargaSerie !== true) avanzarEstado(cargaSerie.id, 'COMPLETADA');
                cargarHistorial();
            }} />}

        {miRecorrido && <MiRecorridoSheet tecnicoId={tecnicoId} onCerrar={() => setMiRecorrido(false)} />}
            {problema && (
            <div className="fixed inset-0 z-[3000] flex items-end md:items-center bg-black/50 md:justify-center md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6" onClick={() => setProblema(null)}>
                <div className="w-full md:max-w-lg md:mx-auto rounded-t-3xl md:rounded-3xl p-5 pb-8 bg-card space-y-2" onClick={e => e.stopPropagation()}>
                    <div className="w-10 h-1 rounded-full mx-auto mb-3 bg-chip md:hidden" />
                    <p className="text-body-lg font-black text-ink">¿Qué pasó?</p>
                    <p className="text-caption text-muted mb-2">{problema.clienteNombre || problema.titulo}</p>
                    {(problema.estado === 'EN_CAMINO' || problema.estado === 'EN_SITIO') && (
                        <button onClick={() => { setNoAtendidoOrden(problema); setProblema(null); }}
                            className="w-full p-4 rounded-2xl text-left bg-chip active:scale-[0.98]">
                            <p className="text-body font-black text-ink">El cliente no atendió</p>
                            <p className="text-caption text-muted">Queda registrado y el admin la reprograma</p>
                        </button>
                    )}
                    <button onClick={() => { setSalida({ modo: 'orden', orden: problema }); setProblema(null); }}
                        className="w-full p-4 rounded-2xl text-left bg-chip active:scale-[0.98]">
                        <p className="text-body font-black text-ink">No puedo ir</p>
                        <p className="text-caption text-muted">La visita vuelve al admin para reasignarla</p>
                    </button>
                    <button onClick={() => { setSalida({ modo: 'mensaje', orden: problema }); setProblema(null); }}
                        className="w-full p-4 rounded-2xl text-left bg-chip active:scale-[0.98]">
                        <p className="text-body font-black text-ink">Avisar algo al admin</p>
                        <p className="text-caption text-muted">Falta un repuesto, otro problema, una duda</p>
                    </button>
                    {ESTADO_ANTERIOR[problema.estado] && (
                        <button onClick={() => { avanzarEstado(problema.id, ESTADO_ANTERIOR[problema.estado].estado); setProblema(null); }}
                            className="w-full p-3 rounded-2xl flex items-center justify-center gap-1.5 font-bold text-label text-muted active:opacity-60">
                            <LuUndo2 size={14} /> Me equivoqué: {ESTADO_ANTERIOR[problema.estado].label.toLowerCase()}
                        </button>
                    )}
                </div>
            </div>
        )}

        {servicioEjecutando && (
            <EjecutarOrdenSheet
                servicio={servicioEjecutando}
                onGuardado={completarOrdenEjecutando}
                onConfirmado={handleConfirmado}
                onCerrar={handleConfirmado}
            />
        )}

        {sumandoA && (
            <ModalRegistrarTrabajo orden={sumandoA.orden} servicio={sumandoA.servicio} tecnicoId={sumandoA.orden.tecnicoId}
                onGuardado={() => { setSumandoA(null); recargar(); }} onCerrar={() => setSumandoA(null)} />
        )}

        {/* Detalle del servicio — link "Ver servicio" desde Completadas (hallazgo 07) */}
        {(cargandoDetalle || servicioDetalle) && (
            <div className="fixed inset-0 z-[3000] flex items-end md:items-center bg-black/50 md:justify-center md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6" onClick={() => setServicioDetalle(null)}>
                <div className="w-full md:max-w-lg md:mx-auto rounded-t-3xl md:rounded-3xl p-5 bg-card border-t border-black/[0.05] dark:border-white/[0.05]"
                    onClick={e => e.stopPropagation()}>
                    <div className="w-10 h-1 rounded-full mx-auto mb-4 bg-chip md:hidden" />
                    {cargandoDetalle ? (
                        <p className="text-center text-muted py-8">Cargando...</p>
                    ) : (
                        <>
                            <h3 className="text-body-lg font-black mb-1 text-ink">
                                Servicio — {servicioDetalle.clienteNombre}
                            </h3>
                            <p className="text-caption text-muted mb-4">#{servicioDetalle.id} · {fechaAR(servicioDetalle.fecha)}</p>
                            <div className="max-h-[calc(var(--vh,1vh)*50)] overflow-y-auto space-y-2 mb-4">
                                {(servicioDetalle.items || []).map((it, idx) => (
                                    <div key={`${it.equipoSerial || 'det'}-${idx}`} className="p-3.5 rounded-xl bg-page border border-black/[0.04] dark:border-white/[0.04]">
                                        <div className="flex justify-between mb-1">
                                            <span className="font-bold text-body text-brand-red">{it.equipoSerial}</span>
                                            <span className="font-black text-body-lg text-ink">${Number(it.costo || 0).toLocaleString('es-AR')}</span>
                                        </div>
                                        <p className="text-caption text-secondary leading-snug">{it.trabajoRealizado}</p>
                                        {it.repuestosUsados?.length > 0 && (
                                            <p className="text-caption text-muted pt-2 mt-2 border-t border-black/[0.04] dark:border-white/[0.04]">
                                                <span className="font-bold">Repuestos: </span>
                                                {it.repuestosUsados.map(r => `${r.cantidad}x ${r.nombre}`).join(', ')}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {/* ¿Te olvidaste un equipo? Se suma el mismo día (21-oct-2026) */}
                            {cerradoHoy(servicioDetalle._orden) && (
                                <button onClick={() => { setSumandoA({ orden: servicioDetalle._orden, servicio: servicioDetalle }); setServicioDetalle(null); }}
                                    className="w-full mb-2 py-3 rounded-xl font-black text-sm text-white active:scale-95 bg-brand-red">
                                    + Sumar equipos
                                </button>
                            )}
                            <button onClick={() => setServicioDetalle(null)}
                                className="w-full py-3 rounded-xl font-bold text-sm text-white active:scale-95 bg-ink dark:text-[#1C1917]">
                                Cerrar
                            </button>
                        </>
                    )}
                </div>
            </div>
        )}
        </>
    );
}
